import { Link } from "react-router-dom";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import type { ApInvoice } from "@/accounts-payable/types";
import {
  AP_TAB,
  invoiceReviewPath,
  invoiceStatusMeta,
} from "@/accounts-payable/utils";
import { formatCentsOrDashIfZero } from "@/core/utils/money";
import { INVOICE_STATUS_TEXT_CLASS } from "./utils";

/**
 * An invoice extracted from the attachment: number, amount, status. The whole
 * row links to the invoice; its hover fill (the table-row hover, blue-5/50)
 * bleeds 8px past the text on each side so the text stays aligned with the
 * file name above.
 */
export const ExtractedInvoiceRow = ({ invoice }: { invoice: ApInvoice }) => {
  const status = invoiceStatusMeta(invoice);
  const hasAmount = invoice.total_amount_cents !== 0;
  const amount = formatCentsOrDashIfZero(invoice.total_amount_cents);
  return (
    <Link
      to={invoiceReviewPath(invoice.id, AP_TAB.emails)}
      aria-label={[
        `Open invoice ${invoice.invoice_number}`,
        hasAmount && amount,
        status.label,
      ]
        .filter(Boolean)
        .join(", ")}
      className="group -mx-2 block rounded-md px-2 py-2 transition-colors hover:bg-blue-5/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      <Stack
        direction="row"
        justify="between"
        align="baseline"
        gap="md"
        fullWidth
      >
        <Typography className="group-hover:underline">
          {invoice.invoice_number}
        </Typography>
        {/* No amount yet (e.g. couldn't read): leave it out, not a dash. */}
        {hasAmount && (
          <Typography textColor="secondary" className="tabular-nums">
            {amount}
          </Typography>
        )}
      </Stack>
      {/* Status as coloured text, not a pill: it sits under a small line of
          type, and a longer label wraps instead of crowding the row. */}
      <Typography
        variant="captionW1SemiBold"
        className={INVOICE_STATUS_TEXT_CLASS[status.variant]}
      >
        {status.label}
      </Typography>
    </Link>
  );
};
